import type { AmazonResale, EbayResale, ResaleResult } from "../services/ResaleService";
type T = (key: string, params?: Record<string, string | number>) => string;

/** GS1 mod-10 check digit, as the server's validGTIN. */
function validGtin(d: string): boolean {
  let sum = 0;
  for (let i = d.length - 2, w = 3; i >= 0; i--, w = w === 3 ? 1 : 3) sum += Number(d[i]) * w;
  return (10 - (sum % 10)) % 10 === Number(d[d.length - 1]);
}

/**
 * The code sent as `upc`. Most product rows keep a retailer item ID there
 * (Walmart item IDs, HD/Lowe's item numbers, Target TCINs), so prefer whichever
 * field is a real GTIN; otherwise send the raw value and let the server reject
 * it (it validates the check digit and restores Dollar General's stripped
 * leading zero). The card still prices by title when there is no UPC.
 */
export function pickResaleCode(product: { sku?: string | null; upc?: string | null } | null | undefined): string {
  if (!product) return "";
  const values = [product.sku, product.upc].map((raw) => String(raw ?? "").replace(/\D/g, ""));
  const gtin = values.find((v) => (v.length >= 12 && v.length <= 14 && validGtin(v)) || (v.length === 11 && validGtin(`0${v}`)));
  return gtin ?? values.find((v) => v.length > 0 && /[1-9]/.test(v)) ?? "";
}

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** "$299", "$89.99" — drops ".00" only. */
export function formatUsd(value: number): string {
  const digits = Math.round(value * 100) % 100 === 0 ? 0 : 2;
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

/** Always two decimals, for the recent-sales list: "$112.50". */
export function formatUsdCents(value: number): string {
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type Link = { label: string; href: string };

/** What one market card shows: the price it sells for there, or why there is none. */
export type MarketView =
  | { kind: "price"; price: number; detail: string; link?: Link }
  | { kind: "message"; message: string; link?: Link };

/** Amazon: current Buy Box, else the 90-day average; with monthly sales when Keepa has them. */
export function amazonView(t: T, amazon: AmazonResale | undefined | null): MarketView {
  const link: Link | undefined = amazon?.url
    ? { label: t("resale.view_amazon"), href: amazon.url }
    : amazon?.searchUrl
    ? { label: t("resale.search_amazon"), href: amazon.searchUrl }
    : undefined;
  const price = isNum(amazon?.buyBox) && amazon!.buyBox! > 0 ? amazon!.buyBox! : isNum(amazon?.avg90) && amazon!.avg90! > 0 ? amazon!.avg90! : null;
  if (price !== null) {
    const parts = [t(isNum(amazon?.buyBox) && amazon!.buyBox! > 0 ? "resale.amazon_buybox" : "resale.amazon_avg90")];
    if (isNum(amazon?.monthlySold) && amazon!.monthlySold! > 0) parts.push(t("resale.amazon_monthly", { n: Math.round(amazon!.monthlySold!) }));
    return { kind: "price", price, detail: parts.join(" · "), link };
  }
  return { kind: "message", message: unavailableMessage(t, "amazon", amazon), link };
}

/** eBay: median of recent sold listings. */
export function ebayView(t: T, ebay: EbayResale | undefined | null, language = "en"): MarketView {
  const link: Link | undefined = ebay?.searchUrl ? { label: t("resale.view_ebay"), href: ebay.searchUrl } : undefined;
  if (isNum(ebay?.median) && ebay!.median! > 0) {
    const count = ebay!.count ?? 0;
    const from = formatShortDate(ebay!.from, language);
    const to = formatShortDate(ebay!.to, language);
    const range = from && to ? (from === to ? from : `${from}–${to}`) : from || to;
    const detail = count > 0 ? t(range ? "resale.ebay_sold_range" : "resale.ebay_sold", { count, range: range ?? "" }) : t("resale.ebay_median");
    return { kind: "price", price: ebay!.median!, detail, link };
  }
  return { kind: "message", message: unavailableMessage(t, "ebay", ebay), link };
}

function unavailableMessage(t: T, market: "amazon" | "ebay", data: AmazonResale | EbayResale | undefined | null): string {
  switch (data?.status) {
    case "no_match":
    case "ok":
    case "partial":
      return t(market === "amazon" ? "resale.no_amazon" : "resale.no_ebay");
    case "skipped":
      if (data.reason === "no_upc") return t("resale.no_barcode");
      if (data.reason === "msrp_under_20") return t("resale.under_20");
      return t("resale.skipped");
    case "budget":
      return t("resale.budget");
    default:
      return t("resale.unavailable");
  }
}

/** True when neither market has a price — the card hides itself. */
export function noMarketData(amazon: MarketView, ebay: MarketView): boolean {
  return amazon.kind === "message" && ebay.kind === "message";
}

export function toMillis(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) return null;
    return value < 1e12 ? value * 1000 : value; // epoch seconds vs ms
  }
  if (/^\d+$/.test(value)) return toMillis(Number(value));
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/** Newest fetchedAt across both markets. */
export function latestFetchedAt(result: ResaleResult | null | undefined): number | null {
  const times = [toMillis(result?.amazon?.fetchedAt), toMillis(result?.ebay?.fetchedAt)].filter((v): v is number => v !== null);
  return times.length ? Math.max(...times) : null;
}

export function formatRelative(t: T, ms: number | null, now = Date.now()): string | null {
  if (ms === null) return null;
  const mins = Math.floor((now - ms) / 60000);
  if (mins < 1) return t("resale.just_now");
  if (mins < 60) return t("resale.time_minutes", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("resale.time_hours", { n: hrs });
  return t("resale.time_days", { n: Math.floor(hrs / 24) });
}

/** "Sep 3" / "3 sept" — falls back to the raw string when it isn't a date. */
export function formatShortDate(value: string | undefined, language: string): string | null {
  if (!value) return null;
  const ms = toMillis(value);
  if (ms === null) return value;
  // eBay sale dates are calendar days ("2026-09-01"), parsed as UTC midnight: format
  // them in UTC so they don't show as the day before west of Greenwich.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  return new Date(ms).toLocaleDateString(language === "es" ? "es-US" : "en-US", { month: "short", day: "numeric", ...(dateOnly ? { timeZone: "UTC" } : {}) });
}
